import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { 
  BookOpen, 
  Check, 
  Sparkles, 
  Zap, 
  RefreshCw, 
  Users, 
  FileText, 
  CreditCard,
  AlertCircle,
  CheckCircle,
  Loader2,
  Unlink,
  ArrowDownUp
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const QuickBooksSettings = ({ limits, onNavigateToPlans }) => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncSettings, setSyncSettings] = useState({
    auto_sync_customers: true,
    auto_sync_invoices: true,
    auto_sync_payments: true
  });

  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  }, []);

  // Check connection status on mount and handle OAuth callback
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/quickbooks/status`, getAuthConfig());
        setConnectionStatus(res.data);
        if (res.data.sync_settings) {
          setSyncSettings(res.data.sync_settings);
        }
      } catch (error) {
        console.error("Failed to check QuickBooks status:", error);
      } finally {
        setLoading(false);
      }
    };

    // Handle OAuth callback from URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get("callback") === "true") {
      if (params.get("connected") === "true") {
        toast.success("QuickBooks connected successfully!");
      } else if (params.get("error")) {
        toast.error(`QuickBooks connection failed: ${params.get("error")}`);
      }
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname + "?tab=quickbooks");
    }

    checkStatus();
  }, [getAuthConfig]);

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/quickbooks/auth`, getAuthConfig());
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (error) {
      toast.error("Failed to initiate QuickBooks connection");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect QuickBooks?")) return;
    
    setDisconnecting(true);
    try {
      await axios.post(`${API}/quickbooks/disconnect`, {}, getAuthConfig());
      setConnectionStatus({ connected: false, status: "disconnected" });
      toast.success("QuickBooks disconnected");
    } catch (error) {
      toast.error("Failed to disconnect QuickBooks");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncCustomers = async (direction = "to-qb") => {
    setSyncing("customers");
    try {
      const endpoint = direction === "to-qb" 
        ? `${API}/quickbooks/sync/customers`
        : `${API}/quickbooks/sync/customers/from-qb`;
      
      const res = await axios.post(endpoint, {}, getAuthConfig());
      
      if (res.data.errors && res.data.errors.length > 0) {
        toast.warning(`Synced ${res.data.synced}/${res.data.total} customers. ${res.data.errors.length} errors.`);
      } else {
        toast.success(`Synced ${res.data.synced} customers successfully!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync customers");
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncInvoices = async () => {
    setSyncing("invoices");
    try {
      const res = await axios.post(`${API}/quickbooks/sync/invoices`, {}, getAuthConfig());
      
      if (res.data.errors && res.data.errors.length > 0) {
        toast.warning(`Synced ${res.data.synced}/${res.data.total} invoices. ${res.data.errors.length} errors.`);
      } else {
        toast.success(`Synced ${res.data.synced} invoices successfully!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync invoices");
    } finally {
      setSyncing(null);
    }
  };

  const handleSettingChange = async (key, value) => {
    const newSettings = { ...syncSettings, [key]: value };
    setSyncSettings(newSettings);
    
    try {
      await axios.put(`${API}/quickbooks/settings`, { [key]: value }, getAuthConfig());
    } catch (error) {
      toast.error("Failed to update settings");
      setSyncSettings(syncSettings); // Revert
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Card className="glass-card border-theme">
          <CardContent className="p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // User doesn't have QuickBooks feature
  if (!limits?.features?.quickbooks_integration) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Card className="glass-card border-theme" data-testid="quickbooks-integration-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#2CA01C]" />
                  QuickBooks Integration
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Connect your QuickBooks account to sync customers, invoices, and payments
                </CardDescription>
              </div>
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                <Sparkles className="w-3 h-3 mr-1" /> Premium Feature
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gradient-to-br from-[#2CA01C]/10 to-[#FFCC00]/10 rounded-xl p-6 border border-[#2CA01C]/20 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-[#2CA01C]/50" />
              <h3 className="text-lg font-medium text-foreground mb-2">Upgrade to Premium</h3>
              <p className="text-muted-foreground mb-4">
                QuickBooks integration is available on the Premium plan. Upgrade to sync your accounting data automatically.
              </p>
              <ul className="text-left max-w-xs mx-auto space-y-2 mb-4">
                <li className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Check className="w-4 h-4 text-[#2CA01C]" />
                  Auto-sync customers & contacts
                </li>
                <li className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Check className="w-4 h-4 text-[#2CA01C]" />
                  Create invoices automatically
                </li>
                <li className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Check className="w-4 h-4 text-[#2CA01C]" />
                  Track payments in real-time
                </li>
                <li className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Check className="w-4 h-4 text-[#2CA01C]" />
                  Expense tracking & reports
                </li>
              </ul>
              <Button className="btn-primary" onClick={onNavigateToPlans}>
                <Zap className="w-4 h-4 mr-2" />
                View Premium Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User has feature but not connected
  if (!connectionStatus?.connected) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Card className="glass-card border-theme" data-testid="quickbooks-integration-card">
          <CardHeader>
            <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#2CA01C]" />
              QuickBooks Integration
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Connect your QuickBooks Online account to sync data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-theme-surface rounded-xl p-6 border border-theme text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-[#2CA01C]" />
              <h3 className="text-lg font-medium text-foreground mb-2">Connect QuickBooks Online</h3>
              <p className="text-muted-foreground mb-4">
                Sync your customer data, create invoices, and track payments automatically with QuickBooks.
              </p>
              <Button 
                className="bg-[#2CA01C] hover:bg-[#228B1A] text-white"
                onClick={handleConnect}
                data-testid="connect-quickbooks-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Connect QuickBooks
              </Button>
              <p className="text-muted-foreground/60 text-xs mt-3">
                You'll be redirected to Intuit to authorize the connection.
              </p>
            </div>

            {/* Features list */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-theme-surface rounded-lg p-4 border border-theme">
                <Users className="w-6 h-6 text-blue-400 mb-2" />
                <h4 className="text-foreground font-medium">Customer Sync</h4>
                <p className="text-muted-foreground text-sm">Two-way sync between Fieldz AI and QuickBooks</p>
              </div>
              <div className="bg-theme-surface rounded-lg p-4 border border-theme">
                <FileText className="w-6 h-6 text-green-400 mb-2" />
                <h4 className="text-foreground font-medium">Invoice Sync</h4>
                <p className="text-muted-foreground text-sm">Automatically create invoices in QuickBooks</p>
              </div>
              <div className="bg-theme-surface rounded-lg p-4 border border-theme">
                <CreditCard className="w-6 h-6 text-purple-400 mb-2" />
                <h4 className="text-foreground font-medium">Payment Recording</h4>
                <p className="text-muted-foreground text-sm">Record payments when jobs are completed</p>
              </div>
              <div className="bg-theme-surface rounded-lg p-4 border border-theme">
                <RefreshCw className="w-6 h-6 text-orange-400 mb-2" />
                <h4 className="text-foreground font-medium">Real-time Updates</h4>
                <p className="text-muted-foreground text-sm">Keep both systems in sync automatically</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connected state - show full integration panel
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Connection Status Card */}
      <Card className="glass-card border-theme" data-testid="quickbooks-integration-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#2CA01C]" />
                QuickBooks Integration
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Manage your QuickBooks Online connection
              </CardDescription>
            </div>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              <CheckCircle className="w-3 h-3 mr-1" /> Connected
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Connection Info */}
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h4 className="text-foreground font-medium">Connected to QuickBooks Online</h4>
                <p className="text-muted-foreground text-sm">
                  Company: <span className="text-foreground">{connectionStatus.company_name || "N/A"}</span>
                </p>
                <p className="text-muted-foreground text-sm">
                  Connected: {connectionStatus.connected_at ? new Date(connectionStatus.connected_at).toLocaleDateString() : "N/A"}
                </p>
                {connectionStatus.last_sync && (
                  <p className="text-muted-foreground text-sm">
                    Last sync: {new Date(connectionStatus.last_sync).toLocaleString()}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4 mr-1" />}
                Disconnect
              </Button>
            </div>
          </div>

          {/* Sync Actions */}
          <div>
            <h4 className="text-foreground font-medium mb-3 flex items-center gap-2">
              <ArrowDownUp className="w-4 h-4 text-[#FFCC00]" />
              Manual Sync
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Customer Sync */}
              <div className="bg-theme-surface rounded-lg p-4 border border-theme">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-5 h-5 text-blue-400" />
                  <span className="text-foreground font-medium">Customers</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-border text-foreground hover:bg-theme-hover"
                    onClick={() => handleSyncCustomers("to-qb")}
                    disabled={syncing !== null}
                    data-testid="sync-customers-to-qb"
                  >
                    {syncing === "customers" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    To QB
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-border text-foreground hover:bg-theme-hover"
                    onClick={() => handleSyncCustomers("from-qb")}
                    disabled={syncing !== null}
                    data-testid="sync-customers-from-qb"
                  >
                    {syncing === "customers" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    From QB
                  </Button>
                </div>
              </div>

              {/* Invoice Sync */}
              <div className="bg-theme-surface rounded-lg p-4 border border-theme">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-5 h-5 text-green-400" />
                  <span className="text-foreground font-medium">Invoices</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border-border text-foreground hover:bg-theme-hover"
                  onClick={handleSyncInvoices}
                  disabled={syncing !== null}
                  data-testid="sync-invoices-btn"
                >
                  {syncing === "invoices" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Sync All Invoices to QB
                </Button>
              </div>
            </div>
          </div>

          {/* Sync Settings */}
          <div>
            <h4 className="text-foreground font-medium mb-3">Auto-Sync Settings</h4>
            <div className="space-y-4 bg-theme-surface rounded-lg p-4 border border-theme">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-foreground">Auto-sync Customers</Label>
                  <p className="text-muted-foreground text-xs">Automatically sync new customers to QuickBooks</p>
                </div>
                <Switch
                  checked={syncSettings.auto_sync_customers}
                  onCheckedChange={(checked) => handleSettingChange("auto_sync_customers", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-foreground">Auto-sync Invoices</Label>
                  <p className="text-muted-foreground text-xs">Automatically create invoices in QuickBooks</p>
                </div>
                <Switch
                  checked={syncSettings.auto_sync_invoices}
                  onCheckedChange={(checked) => handleSettingChange("auto_sync_invoices", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-foreground">Auto-sync Payments</Label>
                  <p className="text-muted-foreground text-xs">Automatically record payments in QuickBooks</p>
                </div>
                <Switch
                  checked={syncSettings.auto_sync_payments}
                  onCheckedChange={(checked) => handleSettingChange("auto_sync_payments", checked)}
                />
              </div>
            </div>
          </div>

          {/* Info Note */}
          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Sync Tips</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Sync customers before syncing invoices</li>
                <li>Use "From QB" to import existing QuickBooks customers</li>
                <li>Payments are automatically synced when jobs are marked as paid</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickBooksSettings;
